import { CommonModule } from '@angular/common'
import { Component, ElementRef, ViewChild } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { UserService } from 'src/app/service/user-info/user.service'

@Component({
  selector: 'ddc-user-profile-pic-dialog',
  templateUrl: './ddc-user-profile-pic-dialog.component.html',
  styleUrls: ['./ddc-user-profile-pic-dialog.component.scss'],
  standalone: true,
  imports: [AngularMaterialsModules, CommonModule, RouterModule],
})
export class DdcUserProfilePicDialogComponent {
  _selectedFile: File = null
  _error: string = null
  _message: string = null
  _isUpdated: boolean = false
  _fileName: string = null
  _previewUrl: string = null
  _isDragOver: boolean = false
  _isUpdating: boolean = false

  @ViewChild('fileInput') fileInput!: ElementRef

  constructor(
    private logger: DdcLoggerService,
    public user_srv: UserService,
    private dialogRef: MatDialogRef<DdcUserProfilePicDialogComponent>,
  ) {}

  triggerFileInput(): void {
    this.fileInput.nativeElement.click()
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this._isDragOver = true
  }

  onDrop(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this._isDragOver = false

    const files = event.dataTransfer?.files
    if (files?.length) {
      this.handleFile(files[0])
    }
  }

  onImageFileSelected(event: any): void {
    this.logger.info('DdcUserProfilePicDialogComponent: onImageFileSelected()')
    const file = event.target.files[0]
    this.handleFile(file)
  }

  private handleFile(file: File): void {
    // Reset previous states
    this._error = null
    this._previewUrl = null

    if (!file) {
      this._error = 'Please select a file'
      return
    }

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg']
    if (!validImageTypes.includes(file.type)) {
      this._error = 'Please select a valid image file (JPG or PNG)'
      return
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      this._error = 'File size should not exceed 5MB'
      return
    }

    this._selectedFile = file

    // Create preview
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>): void => {
      this._previewUrl = e.target?.result as string
    }
    reader.onerror = (): void => {
      this._error = 'Failed to read file. Please try again.'
      this._selectedFile = null
      this._previewUrl = null
    }
    reader.readAsDataURL(file)
  }

  onProfileImageUpload(): void {
    if (!this._selectedFile) return

    this._isUpdating = true
    this._error = null
    this._message = null

    this.user_srv.update_user_profile_pic(this._selectedFile).subscribe({
      next: (res: any) => {
        if (res.error) {
          this.logger.error('DdcUserProfilePicDialogComponent: onProfileImageUpload() | res:', res)
          this._error = res.error
        } else {
          this._message = res.message
          this._isUpdated = true
          setTimeout(() => {
            this.dialogRef.close('dialogClosed')
          }, 1500)
        }
      },
      error: (error: unknown) => {
        this._error = 'Failed to upload image. Please try again.'
        this.logger.error('Profile picture upload error:', error)
      },
      complete: () => {
        this._isUpdating = false
      },
    })
  }
}
